import React, { useEffect, useRef, useState, Suspense, useCallback, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import CustomPopup from '../../common/CustomPopup';
import { Button, Divider, Heading, Icon, ActionIcon, Text } from '../../common';
import { InputText, TextArea } from '../../components/form';
import { CustomButton } from '../../common/commonStyled';
import DesignQuestion from './DesignQuestion';
import maverickPortalhttp from '../../utils/webService/webService';
import maverickPortalApiEndPoint from '../../config/apiEndPoint';
import styled from '@emotion/styled';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { StrictModeDroppable } from '../StrictModeDroppable';
import { faL } from '@fortawesome/free-solid-svg-icons';
import {
  DndContext,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  PointerSensor,
  closestCenter,
  pointerWithin,
  rectIntersection,
  useSensor,
  useSensors,
  DragOverlay,
  MeasuringStrategy,
  defaultDropAnimationSideEffects
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  arrayMove,
  verticalListSortingStrategy,
  defaultAnimateLayoutChanges,
  sortableKeyboardCoordinates
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import SortableTest from './SortableTest';
import PageLoader from '../PageLoader';
import { PiDotsSixVerticalBold } from 'react-icons/pi';

import 'react-toastify/dist/ReactToastify.css';
import './DeleteQuestionToast.css';
import { ToastContainer, toast, Bounce } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleCheck } from '@fortawesome/free-solid-svg-icons';
import { timer } from 'd3';
import SectionDetailsEditor from './components/SectionDetailsEditor';

const VIRTUAL_KEYBOARD_STORAGE_KEY = 'design.form.useVirtualKeyboard';
const QUESTION_DROP_PLACEHOLDER_HEIGHT = 400;

const toShuffleSectionFlag = value => (Number(value) === 1 ? 1 : 0);

const buildShuffleSectionEditPayload = ({
  userId,
  section,
  shuffleQuestion,
  timerValue = '',
  selectedFile,
  mediaType
}) => {
  const formData = new FormData();
  formData.append('user_ID', userId);
  formData.append('QuestionPaperSection_ID', section.questionPaperSection_ID);
  formData.append('sectionName', section.sectionName || '');
  formData.append('sectionDescription', section.sectionDescription || '');
  formData.append('timer', timerValue || section.timer || '');
  formData.append('shuffleQuestion', shuffleQuestion);

  if (selectedFile) {
    formData.append('sectionImage', selectedFile);
    formData.append('fileType', mediaType);
  } else {
    formData.append('sectionImage', section.presignedUrlSectionImage || '');
    formData.append('fileType', section.fileType);
  }

  return formData;
};

const CustomButtont = styled(Button)`
  padding: 2px 1rem;
  color: var(--white);
  buttoncolor: var(--sapGreen);
  cursor: pointer;
`;

// Styled file input components for sections
const SectionFileInputWrapper = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  margin: 12px 0;
  width: 100%;
`;

const SectionFileInputLabel = styled.label`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.475rem 1.125rem;
  background: transparent;
  color: var(--sapGreen);
  border: 1px solid var(--sapGreen);
  border-radius: 0.3125rem;
  font-weight: 700;
  font-size: 12px;
  cursor: pointer;
  transition: box-shadow 250ms ease;
  min-width: 120px;
  text-align: center;
  height: auto;

  &:hover {
    box-shadow: 0px 2px 8px 1px rgba(0, 0, 0, 0.2);
    background: var(--sapGreen);
    color: var(--white);
  }

  &:active {
    transform: translateY(1px);
    box-shadow: 0px 1px 4px rgba(0, 0, 0, 0.15);
  }
`;

const SectionHiddenFileInput = styled.input`
  position: absolute;
  width: 0.1px;
  height: 0.1px;
  opacity: 0;
  overflow: hidden;
  z-index: -1;
`;

const SectionFileNameDisplay = styled.div`
  font-size: 12px;
  color: var(--greyBlue);
  font-weight: 400;
  display: inline-block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 250px;
`;

class PointerSensorWithoutPreventDefault extends PointerSensor {
  static activators = [
    {
      eventName: 'onPointerDown',
      handler: ({ nativeEvent }) => {
        if (!nativeEvent.isPrimary || nativeEvent.button !== 0) {
          return false;
        }
        return shouldHandleEvent(nativeEvent.target);
      }
    }
  ];
}

// Custom Focus Sensor to prevent drag on focusable elements
class FocusSensor extends KeyboardSensor {
  static activators = [
    {
      eventName: 'onKeyDown',
      handler: ({ nativeEvent }) => {
        return shouldHandleEvent(nativeEvent.target);
      }
    }
  ];
}

// Helper function to check if an event should activate dragging
function shouldHandleEvent(element) {
  let cur = element;
  while (cur) {
    if (cur.dataset && cur.dataset.noDnd) {
      return false; // Prevent drag if `data-no-dnd="true"` is present
    }
    cur = cur.parentElement;
  }
  return true;
}

// Custom SmartPointerSensor that ignores interactive elements
class SmartPointerSensor extends PointerSensor {
  static activators = [
    {
      eventName: 'onPointerDown',
      handler: ({ nativeEvent: event }) => {
        if (!event.isPrimary || event.button !== 0 || isInteractiveElement(event.target)) {
          return false;
        }
        return true;
      }
    }
  ];
}

// Function to check if an element is interactive (prevents dragging)
function isInteractiveElement(element) {
  const interactiveElements = ['button', 'input', 'textarea', 'select', 'option'];
  return element?.tagName && interactiveElements.includes(element.tagName.toLowerCase());
}

const resolveId = raw => {
  if (raw === null || raw === undefined) return null;
  if (typeof raw === 'string' || typeof raw === 'number') return raw;
  if (typeof raw === 'object') {
    const candidate =
      raw.value ??
      raw.question_ID ??
      raw.parentQuestion_ID ??
      raw.parentQuestionAttached ??
      raw.id ??
      null;
    return typeof candidate === 'object' ? null : candidate;
  }
  return null;
};

const getParentQuestionIds = question => {
  const parentIds = new Set();

  const collectFromLogicArray = arr => {
    if (!Array.isArray(arr)) return;
    arr.forEach(item => {
      const id = resolveId(
        item?.parentQuestion_ID ??
        item?.parentQuestionAttached ??
        item?.question_ID ??
        item?.selectedQuestion ??
        item?.LikertScaleID ??
        item?.likertQuestion_ID ??
        item?.selectedLikerChocieID
      );
      if (id) parentIds.add(String(id));
    });
  };

  const parentLogic = question?.ParentquestionAttached;
  if (parentLogic) {
    collectFromLogicArray(parentLogic.ANDLogic);
    collectFromLogicArray(parentLogic.ORLogic);
    const flatParentId = resolveId(parentLogic.parentQuestion_ID ?? parentLogic.parentQuestionAttached);
    if (flatParentId) parentIds.add(String(flatParentId));
  }

  collectFromLogicArray(question?.ANDLogic);
  collectFromLogicArray(question?.ORLogic);

  const flatSelected = resolveId(question?.selectedQuestion);
  if (flatSelected) parentIds.add(String(flatSelected));

  return Array.from(parentIds);
};

const isOrderValid = (sections, activeId = null) => {
  const flatQuestions = [];
  sections.forEach(section => {
    if (Array.isArray(section.questionName_array)) {
      section.questionName_array.forEach(q => {
        if (q?.question_ID) {
          flatQuestions.push(q);
        }
      });
    }
  });

  const idToIndex = {};
  flatQuestions.forEach((q, idx) => {
    idToIndex[String(q.question_ID)] = idx;
  });

  const childToParents = {};

  const registerChildParent = (childId, parentId) => {
    if (!childId || !parentId) return;
    if (String(childId) === String(parentId)) return; 
    const key = String(childId);
    if (!childToParents[key]) childToParents[key] = new Set();
    childToParents[key].add(String(parentId));
  };

  flatQuestions.forEach(q => {
    const parentId = q.question_ID;

    if (Array.isArray(q.choiceName_array)) {
      q.choiceName_array.forEach(choice => {
        if (Array.isArray(choice.questionAttached)) {
          choice.questionAttached.forEach(childId => {
            registerChildParent(childId, parentId);
          });
        }
      });
    }

    if (q.CompareValueArray && typeof q.CompareValueArray === 'object') {
      Object.values(q.CompareValueArray).forEach(operationArray => {
        if (Array.isArray(operationArray)) {
          operationArray.forEach(entry => {
            if (
              entry.questionAttached &&
              typeof entry.questionAttached === 'object' &&
              !Array.isArray(entry.questionAttached) &&
              Object.keys(entry.questionAttached).length > 0
            ) {
              Object.keys(entry.questionAttached).forEach(childId => {
                registerChildParent(childId, parentId);
              });
            }
          });
        }
      });
    }

    if (
      q.questionAttached &&
      typeof q.questionAttached === 'object' &&
      !Array.isArray(q.questionAttached) &&
      Object.keys(q.questionAttached).length > 0
    ) {
      Object.keys(q.questionAttached).forEach(childId => {
        registerChildParent(childId, parentId);
      });
    }
  });

  for (const q of flatQuestions) {
    const childKey = String(q.question_ID);
    const childIndex = idToIndex[childKey];

    const allParentIds = new Set([
      ...getParentQuestionIds(q),
      ...(childToParents[childKey] || [])
    ]);

    for (const parentId of allParentIds) {
      const parentIndex = idToIndex[parentId];
      if (parentIndex !== undefined && parentIndex !== -1 && childIndex <= parentIndex) {

        if (activeId == null || childKey === String(activeId) || parentId === String(activeId)) {
          console.warn('[isOrderValid] blocked drag — child', childKey, 'must come after parent', parentId);
          return false;
        }
      }
    }
  }

  return true;
};

const CustomDivider = () => <Divider width="100%" height="1px" margin="1.5rem 0" background="var(--greyBlue)" />;

const TimerInput = ({
  timer,
  onTimerChange,
  sIndex,
  editSection,
  seteditSectionIndex,
  disabled,
  timerMapButton,
  setTimerMapButton
}) => {
  // Local state for timer to prevent re-renders on every keystroke
  const [localTimer, setLocalTimer] = useState(timer || '');
  const [hasInputChanged, setHasInputChanged] = useState(false);

  const timerRef = useRef(null);
  const handleLocalTimerChange = value => {
    setLocalTimer(value);
    // Enable button when user changes input value
    setHasInputChanged(true);
  };
  const handleSetTimer = () => {
    setTimerMapButton(prev => ({
      ...prev,
      [sIndex]: true
    }));
    if (onTimerChange) {
      onTimerChange(sIndex, localTimer);
    }
    // Always set editSectionIndex to show save button when timer is set
    if (editSection) {
      seteditSectionIndex(editSection);
    } else {
      seteditSectionIndex(sIndex);
    }
    // Reset input changed state after setting timer
    setHasInputChanged(false);
  };

  const handleTimerBlur = value => {
    // Clear any pending debounced calls and update immediately on blur
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    if (onTimerChange) {
      onTimerChange(sIndex, value);
    }
    // Also set editSectionIndex on blur if there's a valid timer value
    if (value && Number(value) > 0 && editSection) {
      seteditSectionIndex(editSection);
    }
  };

  // Sync localTimer with timer prop when it changes from parent
  useEffect(() => {
    setLocalTimer(timer || '');
    // Reset input changed state when timer prop changes
    setHasInputChanged(false);
  }, [timer]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
      <Icon style={{ fontSize: '16px', color: '#666' }}>schedule</Icon>
      <span style={{ fontSize: '14px', color: '#666' }}>Set Time:</span>
      <input
        type="number"
        min="0"
        max="999"
        value={localTimer}
        onChange={e => handleLocalTimerChange(e.target.value)}
        onBlur={e => handleTimerBlur(e.target.value)}
        disabled={disabled}
        placeholder="000"
        style={{
          width: '60px',
          // padding: '4px 8px',
          border: '1px solid #ccc',
          borderRadius: '4px',
          fontSize: '14px',
          textAlign: 'center'
        }}
      />
      <span style={{ fontSize: '14px', color: '#666' }}>s</span>
      {!disabled && (
        <Button
          variant="outlined"
          buttonColor="var(--sapGreen)"
          size="small"
          onClick={handleSetTimer}
          disabled={timerMapButton[sIndex] || !hasInputChanged}
          style={{
            padding: '4px 8px',
            fontSize: '12px',
            minWidth: 'auto',
            border: '2px solid #728F01',
            color: timerMapButton[sIndex] || !hasInputChanged ? '#999' : 'white',
            background: timerMapButton[sIndex] || !hasInputChanged ? '#ccc' : '#728F01',
            cursor: timerMapButton[sIndex] || !hasInputChanged ? 'not-allowed' : 'pointer',
            opacity: timerMapButton[sIndex] || !hasInputChanged ? 0.6 : 1
          }}
        >
          {timerMapButton[sIndex] ? 'Timer Set' : 'Set Timer'}
        </Button>
      )}
    </div>
  );
};

const MediaUploadPreview = ({
  onSelectFile,
  handleRemoveFile,
  fileError,
  preview,
  mediaType,
  editSection, // 0 for image, 1 for video
  presignedUrlSectionImage,
  sectionFileType,
  clearSectionMedia,
  sIndex,
  disabled = false,
  fileName
}) => {
  // Use preview if set, otherwise use presignedUrlSectionImage from API
  const displayPreview = preview || presignedUrlSectionImage || '';
  const displayMediaType =
    typeof mediaType === 'number' ? mediaType : typeof sectionFileType === 'number' ? sectionFileType : '';

  return (
    <div>
      {/* Only show input if there is no preview and not disabled */}
      {!displayPreview && !disabled && (
        <SectionFileInputWrapper>
          <SectionFileInputLabel htmlFor={`file-input-${sIndex}`}>
            Choose File
          </SectionFileInputLabel>
          <SectionHiddenFileInput
            id={`file-input-${sIndex}`}
            type="file"
            accept="image/*,video/*,.pdf,.doc,.docx"
            onChange={e => {
              onSelectFile(e, editSection, sIndex);
            }}
            disabled={disabled}
          />
          <SectionFileNameDisplay>
            {fileName || 'No file chosen'}
          </SectionFileNameDisplay>
        </SectionFileInputWrapper>
      )}

      {fileError && <p style={{ color: 'red', fontSize: '14px', fontWeight: 400 }}>{fileError}</p>}
      <div style={{ display: 'flex' }}>
        {displayPreview && (
          <div style={{ marginBottom: '15px', marginTop: '15px' }}>
            <div
              style={{
                position: 'relative',
                display: 'inline-block',
                marginBottom: '10px',
                overflow: 'hidden'
              }}
            >
 {displayMediaType === 0 ? (
                        // Image Preview
                          <img
                  src={displayPreview}
                            width="250"
                            height="150"
                            style={{ objectFit: 'cover', display: 'block', margin: '0', padding: '0' }}
                            alt="Preview"
                          />
                        ) : displayMediaType === 1 ? (
                          // Video Preview
                          <video width="350" height="240" controls style={{ display: 'block', maxWidth: '100%', height: 'auto' }}>
                            <source src={displayPreview}/>
                            Your browser does not support the video tag.
                          </video>
                        ) : displayMediaType === 2 ? (
                          // PDF Preview
                          <iframe
                            src={displayPreview}
                            title="PDF Preview"
                            style={{ width: '100%', height: '500px', display: 'block', border: 'none' }}
                          />
                        ) : null}
              {/* Cross icon: only show if not disabled */}
              {!disabled && (
                <span
                  onClick={e => {
                    handleRemoveFile(e, editSection, sIndex);
                    if (clearSectionMedia) clearSectionMedia(editSection);
                  }}
                  style={{
                    position: 'absolute',
                    top: '4px',
                    right: '6px',
                    fontSize: '18px',
                    fontWeight: 'bold',
                    color: '#fff',
                    backgroundColor: '#f44336',
                    borderRadius: '50%',
                    width: '24px',
                    height: '24px',
                    textAlign: 'center',
                    lineHeight: '24px',
                    cursor: 'pointer',
                    userSelect: 'none'
                  }}
                  title="Remove"
                >
                  ×
                </span>
              )}
            </div>
            {/* Display filename below the uploaded preview */}
            {fileName && (
              <div style={{ fontSize: '14px', color: '#666', marginTop: '8px', fontWeight: 500 }}>
                {fileName}
              </div>
            )}
            {/* Timer field - only show when media is uploaded */}
          </div>
        )}
      </div>
    </div>
  );
};

export default function DesignFormTab({
  task,
  nextTask,
  taskList,
  questionTypeList,
  handleTaskTab,
  sectionIndex,
  ValueTypeChoices,
  addQuestionDisable,
  setAddQuestionDisable,
  questionIndex,
  isTaskDescriptionEditing,
  onTaskDescriptionEdit,
  onSaveTaskDescription
}) {
  const loggedUser = useSelector(state => state.LoginReducer);
  const projectTask_ID = task.projectTask_ID;
  const [deleteTargetSectionKey, setDeleteTargetSectionKey] = useState(null);
  const { projectId } = useParams();
  const [popup, setPopup] = useState(false);
  const [formTask, setFormTask] = useState(task);
  const [formTaskSections, setFormTaskSections] = useState(taskList);
  const dispatch = useDispatch();
  const [isPopupVisible, setIsPopupVisible] = useState(false);
  const [isPopup, setIsPopup] = useState(false);
  //const [popupMessage, setPopupMessage] = useState('');
  const [popupColor, setPopupColor] = useState('');
  const [message, setMessage] = useState(false);
  const addSec = useSelector(state => state.ProjectReducer?.addSection);
  const editSec = useSelector(state => state.ProjectReducer?.editSection);
  const editQues = useSelector(state => state.TaskReducer?.editQuestion);
  const [editSectionIndex, seteditSectionIndex] = useState(null);
  const addQues = useSelector(state => state.TaskReducer?.addQuestion);
  const deleteSec = useSelector(state => state.ProjectReducer?.deleteSec);
  const deleteQues = useSelector(state => state.TaskReducer?.deleteQues);
  const dragQuestion = useSelector(state => state.ProjectReducer?.dragQuestion);
  const dragSection = useSelector(state => state.ProjectReducer?.dragSection);
  const dragQuestionAcrossSections = useSelector(state => state.ProjectReducer?.dragQuestionAcrossSections);

  const [sectionmessage, setsectionMessage] = useState(null);
  const [newQuestionIndex, setNewQuestionIndex] = useState(null);
  const [editQuestionIndex, setEditQuestionIndex] = useState(null);
  const [questionmessage, setquestionMessage] = useState(null);
  const [sectionNameError, setSectionNameError] = useState(false);
  const [sectionSavedStatus, setSectionSavedStatus] = useState([]);
  const [switchvalue, setSwitchValue] = useState(0);
  const [QuestionIndex, setQuestionIndex] = useState(null);
  const [isNewSectionForm, setisNewSectionForm] = useState(false);
  const [addSectionDisable, setAddSectionDisbale] = useState(false);
  const [isSectionSaveLoading, setIsSectionSaveLoading] = useState(false);
  const [editQuestionId, setEditQuestionId] = useState('');
  const [deletesectionmessage, setdeletesectionMessage] = useState(null);
  const [deleteSectionIndex, setDeleteSectionIndex] = useState(null);
  const [deleteQuestionIndex, setDeleteQuestionIndex] = useState(null);
  const [isAddingNewSection, setIsAddingNewSection] = useState(false);
  const [sectionTimeError, setSectionTimeError] = useState(false);
  const [sectionTimeErrorMap, setSectionTimeErrorMap] = useState({}); // { [sIndex]: true/false }

  const initialLoad = useRef(true);
  const suppressPersistedStatusFetchRef = useRef(true);
  const pendingEditQuestionSectionsRef = useRef(null);
  const editQuestionSectionsReadyRef = useRef(false);
  const editQuestionTimeoutRef = useRef(null);
  const sectionShuffleRef = useRef({});
  const [isEditing, setIsEditing] = useState(false);

  // Ignore redux-persist hydrated add/edit statuses on first mount so we don't
  // call getProjectTaskQuestions again after DesignScreen's initial fetch.
  useEffect(() => {
    dispatch({ type: 'CLEAR_EDIT_QUESTION' });
    dispatch({ type: 'CLEAR_ADD_QUESTION' });
    dispatch({ type: 'CLEAR_ADD_SECTION' });
    dispatch({ type: 'CLEAR_EDIT_SECTION' });

    const timer = setTimeout(() => {
      suppressPersistedStatusFetchRef.current = false;
    }, 0);
    return () => clearTimeout(timer);
  }, [dispatch]);
  const [fileError, setFileError] = useState(null);
  const [previewMap, setPreviewMap] = useState({}); // { [sIndex]: previewUrl }
  const [mediaTypeMap, setMediaTypeMap] = useState({}); // { [sIndex]: mediaType }
  const [selectedFileMap, setSelectedFileMap] = useState({}); // { [sIndex]: file }
  const [fileNameMap, setFileNameMap] = useState({}); // { [sIndex]: fileName }
  const [timerMap, setTimerMap] = useState({}); // { [sIndex]: timer in seconds }
  const [timerMapButton, setTimerMapButton] = useState({}); // { [sectionId]: true/false }

  const handleSetEditing = () => {
    setIsEditing(false);
  };

  const [openSections, setOpenSections] = useState(new Set());

  const [draggingItem, setDraggingItem] = useState(null);
  const dragSourceSectionId = useRef(null);
  const dragStartSnapshotRef = useRef(null);

  const [crossSectionHover, setCrossSectionHover] = useState(null);

  const [choiceNameParentQuestions, setChoiceNameParentQuestions] = useState([]);
  const [isVirtualKeyboardEnabled, setIsVirtualKeyboardEnabled] = useState(() => {
    const saved = localStorage.getItem(VIRTUAL_KEYBOARD_STORAGE_KEY);
    return saved === 'true';
  });

  // Show a loader until taskList data is available from the parent
  const shouldShowInitialLoader = !taskList || taskList.length === 0;

  const toggleOpen = (sectionId, preventScroll = false) => {
    setOpenSections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(sectionId)) {
        newSet.delete(sectionId);
      } else {
        newSet.add(sectionId);
        if (!preventScroll) {
          setTimeout(() => {
            const sectionEl = document.querySelector(`[data-section-id="${sectionId}"]`);
            if (sectionEl) {
              sectionEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
          }, 100);
        }
      }
      return newSet;
    });
  };

  useEffect(() => {
    if (newQuestionIndex !== null) {
      const section = formTaskSections[newQuestionIndex];
      const sectionId = section?.questionPaperSection_ID;
      if (sectionId && openSections.has(sectionId)) {

        const sectionEl = document.querySelector(`[data-section-id="${sectionId}"]`);
        if (sectionEl) {
          const lists = sectionEl.querySelectorAll('.questions-list');
          const inner = lists[lists.length - 1];
          if (inner) {
            const target = inner.lastElementChild || inner;
            target.scrollIntoView({ behavior: 'smooth', block: 'center' });
          } else {
            sectionEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }
      }
    }
  }, [newQuestionIndex, openSections, formTaskSections]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8
      }
    }),
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 8
      }
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 250,
        tolerance: 5
      }
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates
    })
  );

  const isSectionBeingDragged = activeId => {
    return formTaskSections.some(s => s.questionPaperSection_ID === activeId);
  };

  useEffect(() => {
    localStorage.setItem(VIRTUAL_KEYBOARD_STORAGE_KEY, String(isVirtualKeyboardEnabled));
  }, [isVirtualKeyboardEnabled]);

  // create a preview as a side effect, whenever selected file is changed
  const onSelectFile = (e, editSection, sIndex) => {
    if (editSection) {
      seteditSectionIndex(editSection);
    }

    // Use consistent section key like in CriteriaBudgetScreen
    const sectionId = getSectionKey(formTaskSections[sIndex], sIndex);

    if (!e.target.files || e.target.files.length === 0) {
      setSelectedFileMap(prev => ({ ...prev, [sectionId]: undefined }));
      setPreviewMap(prev => ({ ...prev, [sectionId]: '' }));
      setMediaTypeMap(prev => ({ ...prev, [sectionId]: '' }));
      setFileNameMap(prev => ({ ...prev, [sectionId]: '' }));
      setTimerMap(prev => ({
        ...prev,
        [sectionId]: ''
      }));
      // Also clear sectionImage and fileType in formTaskSections
      let tempFormTaskSections = [...formTaskSections];
      if (tempFormTaskSections[sIndex]) {
        tempFormTaskSections[sIndex].sectionImage = '';
        tempFormTaskSections[sIndex].fileType = '';
        tempFormTaskSections[sIndex].timer = '';
        setFormTaskSections(tempFormTaskSections);
      }
      return;
    }
    const file = e.target.files[0];
    const maxSizeMB = 10.1 * 1024 * 1024;
    if (file.size > maxSizeMB) {
      setFileError('File size should not exceed 10 MB.');
      setSelectedFileMap(prev => ({ ...prev, [sectionId]: undefined }));
      setPreviewMap(prev => ({ ...prev, [sectionId]: '' }));
      setMediaTypeMap(prev => ({ ...prev, [sectionId]: '' }));
      setFileNameMap(prev => ({ ...prev, [sectionId]: '' }));
      setTimerMap(prev => ({
        ...prev,
        [sectionId]: ''
      }));
      setTimeout(() => setFileError(null), 1500);
      handleRemoveFile();
      return;
    } else {
      setFileError(null);
    }
    const fileType = file.type;
    let currentMediaType = '';
    if (fileType.startsWith('image/')) {
      currentMediaType = 0;
    } else if (fileType.startsWith('video/')) {
      currentMediaType = 1;
    } else if (fileType === 'application/pdf'|| fileName.endsWith('.pdf') || fileName.startsWith('pdf/')) {
      currentMediaType = 2;
    } else {
      setFileError('Unsupported File type');
      setTimeout(() => setFileError(null), 1500);
      setSelectedFileMap(prev => ({ ...prev, [sectionId]: undefined }));
      setPreviewMap(prev => ({ ...prev, [sectionId]: '' }));
      setMediaTypeMap(prev => ({ ...prev, [sectionId]: '' }));
      setFileNameMap(prev => ({ ...prev, [sectionId]: '' }));
      setTimerMap(prev => ({
        ...prev,
        [sectionId]: ''
      }));
      handleRemoveFile();
      return;
    }
    setMediaTypeMap(prev => ({ ...prev, [sectionId]: currentMediaType }));
    setFileNameMap(prev => ({ ...prev, [sectionId]: file.name }));
    const objectUrl = URL.createObjectURL(file);
    setPreviewMap(prev => ({ ...prev, [sectionId]: objectUrl }));
    setSelectedFileMap(prev => ({ ...prev, [sectionId]: file }));
    let tempFormTaskSections = [...formTaskSections];
    if (tempFormTaskSections[sIndex]) {
      tempFormTaskSections[sIndex].sectionImage = file;
      tempFormTaskSections[sIndex].fileType = currentMediaType;
      tempFormTaskSections[sIndex].timer = timerMap[sectionId] || '';
      setFormTaskSections(tempFormTaskSections);
    }
  };
  const handleRemoveFile = (e, editSection, sIndex) => {
    // Clear the file input element to allow new file selection
    const fileInput = document.getElementById(`file-input-${sIndex}`);
    if (fileInput) {
      fileInput.value = '';
    }

    // Use consistent section key like in CriteriaBudgetScreen
    const sectionId = getSectionKey(formTaskSections[sIndex], sIndex);

    setPreviewMap(prev => ({ ...prev, [sectionId]: '' }));
    setMediaTypeMap(prev => ({ ...prev, [sectionId]: '' }));
    setSelectedFileMap(prev => ({ ...prev, [sectionId]: undefined }));
    setFileNameMap(prev => ({ ...prev, [sectionId]: '' }));
    setTimerMap(prev => ({ ...prev, [sectionId]: '' }));

    // Only set editSectionIndex for existing sections (not new ones)
    const section = formTaskSections[sIndex];
    if (editSection && section && !section.isNew) {
      seteditSectionIndex(editSection);
    }

    let tempFormTaskSections = [...formTaskSections];
    if (tempFormTaskSections[sIndex]) {
      tempFormTaskSections[sIndex].sectionImage = '';
      tempFormTaskSections[sIndex].fileType = '';
      tempFormTaskSections[sIndex].presignedUrlSectionImage = '';
      tempFormTaskSections[sIndex].timer = '';
      setFormTaskSections(tempFormTaskSections);
    }

    // Clear timer error when media is removed - no timer required without media
    setSectionTimeErrorMap(prev => ({ ...prev, [sectionId]: false }));
  };

  useEffect(() => {
    if (suppressPersistedStatusFetchRef.current) {
      return;
    }

    if (!addSec?.StatusCode && !editSec?.StatusCode) {
      return;
    }

    if (addSec?.StatusCode == 1) {
      setPopupColor('var(--sapGreen)');
      setsectionMessage('Section Added Successfully!');
    }
    if (addSec?.StatusCode == -1) {
      setPopupColor('red');
      setsectionMessage('Error While Adding Section');
    }

    const timeout = setTimeout(() => {
      setsectionMessage(null);
      seteditSectionIndex(null);
      dispatch({ type: 'CLEAR_ADD_SECTION' });
      dispatch({ type: 'CLEAR_EDIT_SECTION' });
    }, 3000);

    maverickPortalhttp
      .makeRequest(
        `${maverickPortalApiEndPoint.getProjectTask.url}/${loggedUser.userId}/${projectId}/${projectTask_ID}`,
        maverickPortalApiEndPoint.getProjectTask.methodType
      )
      .then(response => {
        let taskData = response.data.Response;

        const mapSectionFromApi = section => ({
          ...section,
          sectionImage: section.presignedUrlSectionImage || '',
          fileType: section.fileType !== undefined && section.fileType !== null ? section.fileType : '',
          sectionName: section.sectionName || '',
          sectionDescription: section.sectionDescription || '',
          timer: section.timer || ''
        });
        const sections = response.data.Response?.GetProjectTaskQuestions?.map(mapSectionFromApi) || [];
        setFormTaskSections(sections);

        // Initialize timerMap with stable keys
        const initialTimerMap = {};
        sections.forEach((section, sIndex) => {
          const key = getSectionKey(section, sIndex); // Use section.questionPaperSection_ID or tempId
          initialTimerMap[key] = section.timer || '';
        });
        setTimerMap(initialTimerMap);
        setIsSectionSaveLoading(false);
        setAddQuestionDisable(false);
      }).catch(() => {
        setIsSectionSaveLoading(false);
        setAddQuestionDisable(false);
      });
  }, [addSec, editSec]);

  useEffect(() => {
    if (suppressPersistedStatusFetchRef.current) {
      return;
    }

    if (addQues && addQues !== undefined) {
      if (addQues?.StatusCode == 1) {
        setIsPopup(true);
        setPopupColor('var(--sapGreen)');
        setquestionMessage('Question Added Successfully!');
      }
      if (addQues?.StatusCode == -1) {
        setIsPopup(true);
        setPopupColor('red');
        setquestionMessage('Error While Adding Question');
      }

      maverickPortalhttp
        .makeRequest(
          `${maverickPortalApiEndPoint.getProjectTask.url}/${loggedUser.userId}/${projectId}/${projectTask_ID}`,
          maverickPortalApiEndPoint.getProjectTask.methodType
        )
        .then(response => {
          let taskData = response.data.Response;

          const mapSectionFromApi = section => ({
            ...section,
            sectionImage: section.presignedUrlSectionImage || '',
            fileType: section.fileType !== undefined && section.fileType !== null ? section.fileType : '',
            sectionName: section.sectionName || '',
            sectionDescription: section.sectionDescription || '',
            timer: section.timer || ''
          });
          setFormTaskSections(taskData?.GetProjectTaskQuestions.map(mapSectionFromApi));
        });

      const timeout = setTimeout(() => {
        setIsPopup(false);
        setquestionMessage(null);
        setNewQuestionIndex(null);
        dispatch({ type: 'CLEAR_ADD_QUESTION' });
      }, 3000);
    }
  }, [addQues]);

  useEffect(() => {
    if (suppressPersistedStatusFetchRef.current) {
      return;
    }

    if (!dragQuestion && !dragSection && !dragQuestionAcrossSections) {
      return;
    }

    refreshTask();
  }, [dragQuestion, dragSection, dragQuestionAcrossSections]);

  useEffect(() => {
    if (editSec?.StatusCode == 1) {
      setIsPopupVisible(true);
      setPopupColor('var(--sapGreen)');
      // setsectionMessage('Section Edited Successfully!');

      // Show success toast notification
      toast.success(
        <div style={{ display: 'flex', alignItems: 'center', padding: '8px 0' }}>
          <FontAwesomeIcon
            icon={faCircleCheck}
            style={{ color: 'var(--primaryColor)', marginRight: '8px', fontSize: '1.5em' }}
          />
          <span>Section Edited Successfully!</span>
        </div>,
        {
          position: 'top-center',
          autoClose: 5000,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: 'light',
          icon: false,
          transition: Bounce
        }
      );

      const timeout = setTimeout(() => {
        setIsPopupVisible(false);
        setsectionMessage(null);
        dispatch({ type: 'CLEAR_EDIT_SECTION' });
      }, 3000);
    }
    if (editSec?.StatusCode == -1) {
      setIsPopupVisible(true);
      setPopupColor('red');
      setsectionMessage('Error While Editing The Section');

      // Show error toast notification
      toast.error(<>Error While Editing The Section</>, {
        position: 'top-center',
        autoClose: 5000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: 'light',
        transition: Bounce
      });

      const timeout = setTimeout(() => {
        setIsPopupVisible(false);
        setsectionMessage(null);
        dispatch({ type: 'CLEAR_EDIT_SECTION' });
      }, 3000);
    }
  }, [editSec]);

  useEffect(() => {
    if (suppressPersistedStatusFetchRef.current) {
      return;
    }

    if (editQues && editQues !== undefined) {
      pendingEditQuestionSectionsRef.current = null;
      editQuestionSectionsReadyRef.current = false;

      if (editQues?.StatusCode == 1) {
        setIsPopup(true);
        setPopupColor('var(--sapGreen)');
        // setquestionMessage('Question Edited Successfully!');
        setquestionMessage(editQues?.StatusDesc);
      }
      if (editQues?.StatusCode == -1) {
        setIsPopup(true);
        setPopupColor('red');
        // setquestionMessage('Question response is already saved. You can not Edit question.');
        setquestionMessage(editQues?.StatusDesc);
      }

      maverickPortalhttp // useEffect(() => {
        .makeRequest(
          `${maverickPortalApiEndPoint.getProjectTask.url}/${loggedUser.userId}/${projectId}/${projectTask_ID}`,
          maverickPortalApiEndPoint.getProjectTask.methodType
        )
        .then(response => {
          const mapSectionFromApi = section => ({
            ...section,
            sectionImage: section.presignedUrlSectionImage || '',
            fileType: section.fileType !== undefined && section.fileType !== null ? section.fileType : '',
            sectionName: section.sectionName || '',
            sectionDescription: section.sectionDescription || '',
            timer: section.timer || ''
          });
          pendingEditQuestionSectionsRef.current = response.data.Response?.GetProjectTaskQuestions.map(mapSectionFromApi) || [];

          if (editQuestionSectionsReadyRef.current) {
            setFormTaskSections(pendingEditQuestionSectionsRef.current);
            pendingEditQuestionSectionsRef.current = null;
          }
        });

      if (editQuestionTimeoutRef.current) {
        clearTimeout(editQuestionTimeoutRef.current);
      }

      editQuestionTimeoutRef.current = setTimeout(() => {
        editQuestionSectionsReadyRef.current = true;
        setIsPopup(false);
        setquestionMessage(null);
        if (pendingEditQuestionSectionsRef.current) {
          setFormTaskSections(pendingEditQuestionSectionsRef.current);
          pendingEditQuestionSectionsRef.current = null;
        }
        dispatch({ type: 'CLEAR_EDIT_QUESTION' });
      }, 2000);

      return () => {
        if (editQuestionTimeoutRef.current) {
          clearTimeout(editQuestionTimeoutRef.current);
        }
      };
    }
  }, [editQues]);

  const toastDisplayed = useRef(false);
  useEffect(() => {
    if (initialLoad.current) {
      initialLoad.current = false;
      return;
    }

    if (deleteSec?.StatusCode && !toastDisplayed.current) {
      const message = deleteSec?.StatusDesc;

      if (deleteSec?.StatusCode === '-1') {
        toast.error(<>{message}</>, {
          position: 'top-center',
          autoClose: 5000,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: 'light',
          transition: Bounce
        });
        setIsPopup(true);
      } else if (deleteSec?.StatusCode === '1') {
        toast.success(
          <div style={{ display: 'flex', alignItems: 'center', padding: '8px 0' }}>
            <FontAwesomeIcon
              icon={faCircleCheck}
              style={{ color: 'var(--primaryColor)', marginRight: '8px', fontSize: '1.5em' }}
            />
            <span>{message}</span>
          </div>,
          {
            position: 'top-center',
            autoClose: 5000,
            hideProgressBar: true,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: 'light',
            icon: false,
            transition: Bounce
          }
        );
        setIsPopup(true);
      }

      toastDisplayed.current = true;

      setTimeout(() => {
        setIsPopup(false);
        dispatch({ type: 'CLEAR_DELETE_SECTION' });
        toastDisplayed.current = false;
      }, 4500);

      maverickPortalhttp
        .makeRequest(
          `${maverickPortalApiEndPoint.getProjectTask.url}/${loggedUser.userId}/${projectId}/${projectTask_ID}`,
          maverickPortalApiEndPoint.getProjectTask.methodType
        )
        .then(response => {
          let taskData = response.data.Response;

          const mapSectionFromApi = section => ({
            ...section,
            sectionImage: section.presignedUrlSectionImage || '',
            fileType: section.fileType !== undefined && section.fileType !== null ? section.fileType : '',
            sectionName: section.sectionName || '',
            sectionDescription: section.sectionDescription || '',
            timer: section.timer || ''
          });
          setFormTaskSections(taskData?.GetProjectTaskQuestions.map(mapSectionFromApi));
        });
    }
  }, [deleteSec]);

  useEffect(() => {
    if (initialLoad.current) {
      initialLoad.current = false;
      return;
    }

    if (deleteQues?.StatusCode && !toastDisplayed.current) {
      const message = deleteQues?.StatusDesc;

      if (deleteQues?.StatusCode === '-1') {
        toast.error(<>{message}</>, {
          position: 'top-center',
          autoClose: 5000,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: 'light',
          transition: Bounce
        });
        setIsPopup(true);
      } else if (deleteQues?.StatusCode === '1') {
        toast.success(
          <div style={{ display: 'flex', alignItems: 'center', padding: '8px 0' }}>
            <FontAwesomeIcon
              icon={faCircleCheck}
              style={{ color: 'var(--primaryColor)', marginRight: '8px', fontSize: '1.5em' }}
            />
            <span>{message}</span>
          </div>,
          {
            position: 'top-center',
            autoClose: 5000,
            hideProgressBar: true,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: 'light',
            icon: false,
            transition: Bounce
          }
        );
        setIsPopup(true);
      }

      toastDisplayed.current = true;

      setTimeout(() => {
        setIsPopup(false);
        dispatch({ type: 'CLEAR_DELETE_QUESTION' });
        toastDisplayed.current = false;
      }, 4500);

      maverickPortalhttp
        .makeRequest(
          `${maverickPortalApiEndPoint.getProjectTask.url}/${loggedUser.userId}/${projectId}/${projectTask_ID}`,
          maverickPortalApiEndPoint.getProjectTask.methodType
        )
        .then(response => {
          let taskData = response.data.Response;

          const mapSectionFromApi = section => ({
            ...section,
            sectionImage: section.presignedUrlSectionImage || '',
            fileType: section.fileType !== undefined && section.fileType !== null ? section.fileType : '',
            sectionName: section.sectionName || '',
            sectionDescription: section.sectionDescription || '',
            timer: section.timer || ''
          });
          setFormTaskSections(taskData?.GetProjectTaskQuestions.map(mapSectionFromApi));
        });

      const timeout = setTimeout(() => {
        setIsPopup(false);
        setquestionMessage(null);
        dispatch({ type: 'CLEAR_DELETE_QUESTION' });
        // getCriteriaBudgetQuestion();
      }, 3000);
    }
  }, [deleteQues]);

  let questionTypes = questionTypeList?.map(questionType => {
    return questionType.questionType_ID != 9 && questionType.questionType_ID != 10
      ? { ...questionType, label: questionType.questionTypeName, value: questionType.questionType_ID }
      : null;
  });

  questionTypes = questionTypes?.filter(function(questionType) {
    return questionType != null;
  });

  const getSectionShuffleValue = section => {
    if (section?.shuffleQuestion !== undefined && section?.shuffleQuestion !== null && section?.shuffleQuestion !== '') {
      return toShuffleSectionFlag(section.shuffleQuestion);
    }
    const sectionId = section?.questionPaperSection_ID;
    if (sectionId != null && sectionShuffleRef.current[sectionId] != null) {
      return sectionShuffleRef.current[sectionId];
    }
    return (section?.questionName_array || []).some(question => Number(question?.shuffleQuestion) === 1) ? 1 : 0;
  };

  const isAppliedLogicFlag = value => value != null && Number(value) !== 0;

  const isSectionShuffleDisabled = section =>
    !section?.questionPaperSection_ID ||
    (section?.questionName_array || []).some(
      question => isAppliedLogicFlag(question?.isLogicApplied) || isAppliedLogicFlag(question?.isSkipLogicApplied)
    );

  const applySectionShuffle = (sections, sectionId, shuffleQuestion) =>
    sections.map(item =>
      item.questionPaperSection_ID === sectionId
        ? {
            ...item,
            shuffleQuestion,
            questionName_array: (item.questionName_array || []).map(question => ({
              ...question,
              shuffleQuestion
            }))
          }
        : item
    );

  const handleShuffleSectionToggle = (sIndex, checked) => {
    const section = formTaskSections[sIndex];
    if (
      section?.isNew ||
      !section?.questionPaperSection_ID ||
      (section?.questionName_array || []).length < 2 ||
      isSectionShuffleDisabled(section)
    ) {
      return;
    }

    const shuffleQuestion = checked ? 1 : 0;
    const sectionId = section.questionPaperSection_ID;
    const sectionKey = getSectionKey(section, sIndex);

    sectionShuffleRef.current[sectionId] = shuffleQuestion;
    setFormTaskSections(prev => applySectionShuffle(prev, sectionId, shuffleQuestion));

    dispatch({
      type: 'EDIT_SECTION',
      payload: buildShuffleSectionEditPayload({
        userId: loggedUser.userId,
        section,
        shuffleQuestion,
        timerValue: timerMap[sectionKey] || '',
        selectedFile: selectedFileMap[sectionKey],
        mediaType: mediaTypeMap[sectionKey]
      })
    });
  };

  useEffect(() => {
    (taskList || []).forEach(section => {
      const sectionId = section?.questionPaperSection_ID;
      if (
        sectionId != null &&
        section?.shuffleQuestion !== undefined &&
        section?.shuffleQuestion !== null &&
        section?.shuffleQuestion !== ''
      ) {
        sectionShuffleRef.current[sectionId] = toShuffleSectionFlag(section.shuffleQuestion);
      }
    });
    setFormTaskSections(taskList);

    // Preserve accordion open/close state when taskList updates, instead of
    // force-closing all sections. This prevents all accordions from collapsing
    // when a question is edited or closed.
    setOpenSections(prevOpen => {
      // Nothing open yet, nothing to reconcile
      if (!prevOpen || prevOpen.size === 0) {
        return prevOpen;
      }

      const next = new Set();

      // Valid section IDs coming from the latest taskList
      const validIds = new Set(
        (taskList || [])
          .map(section => section && section.questionPaperSection_ID)
          .filter(id => id !== null && id !== undefined)
      );

      // Keep open state only for sections that still exist.
      prevOpen.forEach(id => {
        // Keep entries for current sections; also keep "null/undefined" which
        // represents a single new (unsaved) section, if any.
        if (id == null || validIds.has(id)) {
          next.add(id);
        }
      });

      return next;
    });
  }, [taskList]);

  useEffect(() => {
    if (!Array.isArray(formTaskSections) || formTaskSections.length === 0) {
      return;
    }

    setTimerMap(prev => {
      const next = { ...prev };
      let changed = false;

      formTaskSections.forEach((section, sIndex) => {
        const key = getSectionKey(section, sIndex);
        const sectionTimer = section?.timer;

        if (sectionTimer !== undefined && sectionTimer !== null && String(sectionTimer) !== '') {
          const normalized = String(sectionTimer);
          if (next[key] !== normalized) {
            next[key] = normalized;
            changed = true;
          }
        } else if (!(key in next)) {
          next[key] = '';
          changed = true;
        }
      });

      return changed ? next : prev;
    });

    setTimerMapButton(prev => {
      const next = { ...prev };
      let changed = false;

      formTaskSections.forEach((section, sIndex) => {
        const key = getSectionKey(section, sIndex);
        if (section?.timer !== undefined && section?.timer !== null && String(section.timer) !== '') {
          const isSet = Number(section.timer) > 0;
          if (next[key] !== isSet) {
            next[key] = isSet;
            changed = true;
          }
        }
      });

      return changed ? next : prev;
    });
  }, [formTaskSections]);

  useEffect(() => {
    if (formTaskSections) {
      if (formTaskSections.length > 0) {
        const tempParentQuestions = [];
        formTaskSections.forEach((section, index) => {
          section?.questionName_array?.forEach(question => {
            if (question?.choiceName_array?.length > 0) {
              question.choiceName_array.forEach(choice => {
                if (Array.isArray(choice?.questionAttached)) {
                  if (choice?.questionAttached?.length > 0) {
                    tempParentQuestions.push({ choice, question, index });
                  }
                } else {
                  if (choice?.questionAttached) {
                    tempParentQuestions.push({ choice, question, index });
                  }
                }

                if (Array.isArray(choice?.sectionAttached)) {
                  if (choice?.sectionAttached?.length > 0) {
                    tempParentQuestions.push({ choice, question, index });
                  }
                }
              });
            }
            if (question?.questionAttached) {
              if (Array.isArray(question?.questionAttached)) {
                if (question?.questionAttached?.length > 0) {
                  tempParentQuestions.push({ questionAttached: question?.questionAttached, question, index });
                }
              } else {
                tempParentQuestions.push({ questionAttached: question?.questionAttached, question, index });
              }
            }

            if (question?.CompareValueArray) {
              const operations = question.MSTCompatibility_IDs;

              Object.entries(question?.CompareValueArray).forEach(([key, valueArray]) => {
                valueArray.forEach(value => {
                  if (Array.isArray(value?.questionAttached)) {
                    if (value.questionAttached.length > 0) {
                      tempParentQuestions.push({ valueChoice, question, index });
                    }
                  } else {
                    if (value?.questionAttached && Object.keys(value.questionAttached).length > 0) {
                      const operation = operations.find(item => item.id == key);

                      tempParentQuestions.push({ comparibility_ID: operation, valueChoice: value, question, index });
                    }
                  }
                });
              });
            }
          });
        });

        setChoiceNameParentQuestions(tempParentQuestions);

        // ✅ set only once
      }
    }
  }, [formTaskSections]);

  useEffect(() => {
    setTimeout(() => {
      setisNewSectionForm(false);
    }, 3000);
  }, [isNewSectionForm]);

  const [showSaveButton, setShowSaveButton] = useState(false);

  const createSection = () => {
    setIsAddingNewSection(true);
    // let tempFormTaskSections = formTaskSections;
    // tempFormTaskSections = [
    //   ...tempFormTaskSections,
    //   { sectionName: '', sectionDescription: '', questionName_array: [], isNew: true }
    // ];

    // setFormTaskSections(tempFormTaskSections);
    // setShowSaveButton(true);
    // setSectionNameError(false);

    setFormTaskSections(prevList => [
      ...prevList,
      {
        sectionName: '',
        sectionDescription: '',
        questionName_array: [],
        isNew: true,
        sectionImage: '',
        fileType: null,
        timer: ''
      }
    ]);
    setShowSaveButton(true);
    setAddSectionDisbale(true);
    setSectionNameError(false);
    setSectionSavedStatus(prevStatus => [...prevStatus, false]);
    setPreviewMap({});
    setSelectedFileMap({});
    setMediaTypeMap({});
    setFileNameMap({});
    const tempId = 'new-' + Date.now();
    setTimerMap(prev => ({
      ...prev,
      [tempId]: '' // or default timer value for the new section
    }));
  };
  const closeHandler = e => {
    setPopup(e);
    if (!e) {
      setDeleteTargetSectionKey(null);
    }
  };

  const handleDragStart = event => {
    const { active } = event;
    setDraggingItem(active.id);
    setCrossSectionHover(null);

    dragStartSnapshotRef.current = formTaskSections.map(s => ({
      ...s,
      questionName_array: Array.isArray(s.questionName_array) ? [...s.questionName_array] : s.questionName_array
    }));

    const sourceSection = formTaskSections.find(s =>
      s.questionName_array.some(q => q.question_ID === active.id)
    );
    dragSourceSectionId.current = sourceSection?.questionPaperSection_ID ?? null;

    // Collapse all sections ONLY if a SECTION is being dragged
    if (isSectionBeingDragged(active.id)) {
      setOpenSections(new Set());
    }
  };

  const handleDragOver = event => {
    const { active, over } = event;
    if (!over || active.id === over.id) {
      setCrossSectionHover(null);
      return;
    }

    const sourceSectionCurrent = formTaskSections.find(s =>
      s.questionName_array.some(q => q.question_ID === active.id)
    );
    const destSectionCurrent =
      formTaskSections.find(s => s.questionName_array.some(q => q.question_ID === over.id)) ||
      formTaskSections.find(s => s.questionPaperSection_ID === over.id);

    if (!sourceSectionCurrent || !destSectionCurrent) {
      setCrossSectionHover(null);
      return;
    }

    if (sourceSectionCurrent.questionPaperSection_ID === destSectionCurrent.questionPaperSection_ID) {
      setCrossSectionHover(null);
      return;
    }

    setOpenSections(prev => {
      if (prev.has(destSectionCurrent.questionPaperSection_ID)) return prev;
      const next = new Set(prev);
      next.add(destSectionCurrent.questionPaperSection_ID);
      return next;
    });

    const destIndexByQuestion = destSectionCurrent.questionName_array.findIndex(
      q => q.question_ID === over.id
    );

    let destIndex;
    if (destIndexByQuestion !== -1) {
      const overRectTop = over?.rect?.top ?? 0;
      const overRectHeight = over?.rect?.height ?? 0;
      const overRectBottom = overRectTop + overRectHeight;
      const overMidY = overRectTop + overRectHeight / 2;

      const activeRectTop = active?.rect?.current?.translated?.top ?? active?.rect?.current?.initial?.top ?? 0;
      const activeRectHeight = active?.rect?.current?.translated?.height ?? active?.rect?.current?.initial?.height ?? 0;
      const activeRectBottom = activeRectTop + activeRectHeight;
      const activeMidY = activeRectTop + activeRectHeight / 2;

      const isFirstQuestion = destIndexByQuestion === 0;
      const isLastQuestion = destIndexByQuestion === destSectionCurrent.questionName_array.length - 1;

      if (isFirstQuestion && activeRectBottom <= overRectTop + overRectHeight * 0.5) {
        destIndex = 0;
      } else if (isLastQuestion && activeRectTop >= overRectBottom - overRectHeight * 0.5) {
        destIndex = destSectionCurrent.questionName_array.length;
      } else {
        const HYSTERESIS_PX = 12;
        const prevHoverIndex =
          crossSectionHover && crossSectionHover.sectionId === destSectionCurrent.questionPaperSection_ID
            ? crossSectionHover.index
            : null;

        const wasAfter = prevHoverIndex === destIndexByQuestion + 1;

        if (wasAfter) {
          destIndex = activeMidY > overMidY - HYSTERESIS_PX ? destIndexByQuestion + 1 : destIndexByQuestion;
        } else {
          destIndex = activeMidY > overMidY + HYSTERESIS_PX ? destIndexByQuestion + 1 : destIndexByQuestion;
        }
      }

      if (destIndex < 0) destIndex = 0;
      if (destIndex > destSectionCurrent.questionName_array.length) {
        destIndex = destSectionCurrent.questionName_array.length;
      }
    } else {
      destIndex = destSectionCurrent.questionName_array.length;
    }

    setCrossSectionHover(prev => {
      if (
        prev &&
        prev.sectionId === destSectionCurrent.questionPaperSection_ID &&
        prev.index === destIndex
      ) {
        return prev;
      }
      return { sectionId: destSectionCurrent.questionPaperSection_ID, index: destIndex };
    });
  };

  const moveQuestionWithinSection = (sections, sectionId, sourceIndex, destinationIndex) => {
    return sections.map(s => {
      if (s.questionPaperSection_ID !== sectionId) return s;
      const newArray = [...s.questionName_array];
      const [movedQuestion] = newArray.splice(sourceIndex, 1);
      newArray.splice(destinationIndex, 0, movedQuestion);
      return { ...s, questionName_array: newArray };
    });
  };

  const moveQuestionAcrossSections = (
    sections,
    sourceSectionId,
    destinationSectionId,
    sourceIndex,
    destinationIndex
  ) => {
    let movedQuestion = null;

    const pass1 = sections.map(s => {
      if (s.questionPaperSection_ID !== sourceSectionId) return s;
      const newArray = [...s.questionName_array];
      [movedQuestion] = newArray.splice(sourceIndex, 1);
      return { ...s, questionName_array: newArray };
    });

    if (!movedQuestion) return sections;

    return pass1.map(s => {
      if (s.questionPaperSection_ID !== destinationSectionId) return s;
      const newArray = [...s.questionName_array];
      newArray.splice(destinationIndex, 0, movedQuestion);
      return { ...s, questionName_array: newArray };
    });
  };

  const moveSection = (sections, activeId, overId) => {
    const sectionIndex = sections.findIndex(s => s.questionPaperSection_ID === activeId);
    const newSectionIndex = sections.findIndex(s => s.questionPaperSection_ID === overId);

    if (sectionIndex !== -1 && newSectionIndex !== -1) {
      return arrayMove(sections, sectionIndex, newSectionIndex);
    }

    return sections;
  };

  const handleDragEnd = event => {
    setDraggingItem(null);
    const crossSectionHoverAtDrop = crossSectionHover;
    setCrossSectionHover(null);
    const { active, over } = event;
    if (!over) return;

    setFormTaskSections(prevSections => {
      let updatedSections = [...prevSections];

      // Check if the dragged item is a section
      const isSectionDrag = prevSections.some(s => s.questionPaperSection_ID === active.id);

      if (isSectionDrag) {
        if (active.id === over.id) return updatedSections;

        // Moving sections
        updatedSections = moveSection(updatedSections, active.id, over.id);

        if (!isOrderValid(updatedSections, active.id)) {
          toast.error("Cannot move this section here: Check conditionality order.", {
            position: "top-right",
            autoClose: 5000,
            hideProgressBar: true,
            closeOnClick: true,
            theme: "light"
          });
          return dragStartSnapshotRef.current || prevSections;
        }

        // API call for section drag and drop
        dispatch({
          type: 'DRAG_SECTION',
          payload: {
            user_ID: loggedUser.userId,
            sections: updatedSections.map((section, index) => ({
              questionPaperSection_ID: section.questionPaperSection_ID.toString(),
              sectionOrder: (index + 1).toString()
            }))
          }
        });
      } else {
        // Use source section captured at drag start as canonical source
        let sourceSection =
          updatedSections.find(s => s.questionPaperSection_ID === dragSourceSectionId.current) ||
          updatedSections.find(s => s.questionName_array.some(q => q.question_ID === active.id));

        const destinationSection =
          updatedSections.find(s => s.questionName_array.some(q => q.question_ID === over.id)) ||
          updatedSections.find(s => s.questionPaperSection_ID === over.id);

        if (!sourceSection || !destinationSection) return updatedSections;

        const isAcrossSection =
          sourceSection.questionPaperSection_ID !== destinationSection.questionPaperSection_ID;

        if (active.id === over.id) {
          if (isAcrossSection) {
            if (!isOrderValid(updatedSections, active.id)) {
              toast.error("Cannot place this question here: Check conditionality order.", {
                position: "top-right",
                autoClose: 5000,
                hideProgressBar: true,
                closeOnClick: true,
                theme: "light"
              });
              return dragStartSnapshotRef.current || prevSections;
            }

            dispatch({
              type: 'DRAG_QUESTION_ACROSS_SECTIONS',
              payload: {
                user_ID: loggedUser.userId,
                questionsPaperSections: [
                  {
                    section_ID: sourceSection.questionPaperSection_ID,
                    questions: updatedSections
                      .find(s => s.questionPaperSection_ID === sourceSection.questionPaperSection_ID)
                      ?.questionName_array.map((q, index) => ({
                        question_ID: q.question_ID,
                        questionOrder: index + 1
                      }))
                  },
                  {
                    section_ID: destinationSection.questionPaperSection_ID,
                    questions: updatedSections
                      .find(s => s.questionPaperSection_ID === destinationSection.questionPaperSection_ID)
                      ?.questionName_array.map((q, index) => ({
                        question_ID: q.question_ID,
                        questionOrder: index + 1
                      }))
                  }
                ]
              }
            });
          }

          return updatedSections;
        }

        const sourceIndex = sourceSection.questionName_array.findIndex(q => q.question_ID === active.id);

        let destinationIndex;
        if (
          isAcrossSection &&
          crossSectionHover &&
          crossSectionHover.sectionId === destinationSection.questionPaperSection_ID
        ) {
          destinationIndex = crossSectionHover.index;
        } else {
          const destinationIndexRaw = destinationSection.questionName_array.findIndex(q => q.question_ID === over.id);
          destinationIndex = destinationIndexRaw === -1 ? destinationSection.questionName_array.length : destinationIndexRaw;
        }

        if (!isAcrossSection) {
          // Moving within the same section
          updatedSections = moveQuestionWithinSection(
            updatedSections,
            sourceSection.questionPaperSection_ID,
            sourceIndex,
            destinationIndex
          );

          if (!isOrderValid(updatedSections, active.id)) {
            toast.error("Cannot place this question here: Check conditionality order.", {
              position: "top-right",
              autoClose: 5000,
              hideProgressBar: true,
              closeOnClick: true,
              theme: "light"
            });
            return dragStartSnapshotRef.current || prevSections;
          }

          dispatch({
            type: 'DRAG_QUESTION',
            payload: {
              user_ID: loggedUser.userId,
              questions: updatedSections
                .find(s => s.questionPaperSection_ID === sourceSection.questionPaperSection_ID)
                ?.questionName_array.map((q, index) => ({
                  question_ID: q.question_ID,
                  questionOrder: index + 1
                }))
            }
          });
        } else {
          // Moving across sections
          if (sourceIndex >= 0) {
            updatedSections = moveQuestionAcrossSections(
              updatedSections,
              sourceSection.questionPaperSection_ID,
              destinationSection.questionPaperSection_ID,
              sourceIndex,
              destinationIndex
            );
          }

          if (!isOrderValid(updatedSections, active.id)) {
            toast.error("Cannot place this question here: Check conditionality order.", {
              position: "top-right",
              autoClose: 5000,
              hideProgressBar: true,
              closeOnClick: true,
              theme: "light"
            });
            return dragStartSnapshotRef.current || prevSections;
          }

          dispatch({
            type: 'DRAG_QUESTION_ACROSS_SECTIONS',
            payload: {
              user_ID: loggedUser.userId,
              questionsPaperSections: [
                {
                  section_ID: sourceSection.questionPaperSection_ID,
                  questions: updatedSections
                    .find(s => s.questionPaperSection_ID === sourceSection.questionPaperSection_ID)
                    ?.questionName_array.map((q, index) => ({
                      question_ID: q.question_ID,
                      questionOrder: index + 1
                    }))
                },
                {
                  section_ID: destinationSection.questionPaperSection_ID,
                  questions: updatedSections
                    .find(s => s.questionPaperSection_ID === destinationSection.questionPaperSection_ID)
                    ?.questionName_array.map((q, index) => ({
                      question_ID: q.question_ID,
                      questionOrder: index + 1
                    }))
                }
              ]
            }
          });
        }
      }

      return updatedSections;
    });

  //  setOpenSections(new Set());
  };

  const storeSection = (sectionIndex, _field, _value) => {
    setIsAddingNewSection(false);
    const section = formTaskSections[sectionIndex];
    const sectionKey = getSectionKey(section, sectionIndex);
    // Only validate timer if media is present
    if (
      (previewMap[sectionKey] || section.presignedUrlSectionImage) &&
      (!timerMap[sectionKey] || Number(timerMap[sectionKey]) <= 0)
    ) {
      setSectionTimeError(true);
    }

    setFormTaskSections(prevList => {
      return prevList.map((section, i) => (i === sectionIndex ? { ...section, [_field]: _value } : section));
    });
    if (section?.sectionName || section?.sectionDescription) {
      const formData = new FormData();
      formData.append('user_ID', loggedUser.userId);
      formData.append('sectionName', _field == 'sectionName' ? _value : formTaskSections[sectionIndex].sectionName);
      formData.append('sectionDescription', formTaskSections[sectionIndex].sectionDescription);
      formData.append('sectionImage', section.sectionImage);
      formData.append('fileType', section.fileType);
      formData.append('timer', timerMap[sectionKey] || section.timer || '');
      formData.append('projectsTask_ID', formTask.projectTask_ID);
      formData.append('project_ID', projectId);
      formData.append('projectScreener_ID', '');
      formData.append('parentType', 'task');
      formData.append('sectionOrder', sectionIndex + 1); // Keep section order 1-based to match drag payload
      if (selectedFileMap[sectionKey]) {
        formData.append('sectionImage', selectedFileMap[sectionKey]); // This will be sent as binary
        formData.append('fileType', mediaTypeMap[sectionKey]);
      }

      setIsSectionSaveLoading(true);
      setAddQuestionDisable(true);
      dispatch({ type: 'ADD_SECTION', payload: formData });

      const updatedStatus = [...sectionSavedStatus];
      updatedStatus[sectionIndex] = true; // Mark the section at index as saved
      setSectionSavedStatus(updatedStatus);
    } else {
      console.log('error');
    }
  };

  const updateSection = (sectionIndex, _field, _value) => {
    let tempFormTaskSections = [...formTaskSections];
    tempFormTaskSections[sectionIndex] = { ...tempFormTaskSections[sectionIndex], [_field]: _value };
    const section = tempFormTaskSections[sectionIndex];
    const sectionKey = getSectionKey(section, sectionIndex);
    // Only validate timer if media is present
    if (
      (previewMap[sectionKey] || section.presignedUrlSectionImage) &&
      (!timerMap[sectionKey] || Number(timerMap[sectionKey]) <= 0)
    ) {
      setSectionTimeError(true);
    }

    if (_field == 'sectionName') {
      if (_value == '') {
        setMessage(true);
      } else {
        setMessage(false);
      }
    }
    if (_field == 'sectionDescription') {
      if (_value == '') {
        setMessage(true);
      } else {
        setMessage(false);
      }
    }

    setFormTaskSections(tempFormTaskSections);

    const formData = new FormData();
    formData.append('user_ID', loggedUser.userId);
    formData.append('QuestionPaperSection_ID', section.questionPaperSection_ID);
    formData.append('sectionName', _field == 'sectionName' ? _value : section.sectionName);
    formData.append('sectionDescription', _field == 'sectionDescription' ? _value : section.sectionDescription);
    formData.append('timer', timerMap[sectionKey] || section.timer || '');
    formData.append('shuffleQuestion', getSectionShuffleValue(section));

    if (selectedFileMap[sectionKey]) {
      formData.append('sectionImage', selectedFileMap[sectionKey]);
      formData.append('fileType', mediaTypeMap[sectionKey]);
    } else {
      formData.append('sectionImage', section.presignedUrlSectionImage || '');
      formData.append('fileType', section.fileType);
    }

    // Now dispatch or call your API with formData
    dispatch({ type: 'EDIT_SECTION', payload: formData });

    setShowSaveButton(false);
    // setEditedSectionIndex(null);
  };

  const createQuestion = sectionIndex => {
    setIsEditing(true);
    let tempFormTaskSections = formTaskSections.slice();

    !tempFormTaskSections[sectionIndex].hasOwnProperty('questionName_array') &&
      (tempFormTaskSections[sectionIndex] = { ...tempFormTaskSections[sectionIndex], questionName_array: [] });

    let tempQuestionArray = tempFormTaskSections[sectionIndex].questionName_array;
    tempQuestionArray.push({
      question_ID: null,
      questionName: null,
      questionDescription: null,
      5: switchvalue,
      isEdit: true,
      questionType_ID: 1,
      oldQuestionType_ID: 1,
      characterLimit: 500,
      maxFileSizeInMB: 10,
      numberOfFiles: 1,
      savedOption: null,
      choiceName_array: [],
      ValueTypeChoices: [],
      likertScaleName_array: [
        {
          questionLikertScale_ID: null,
          questionLikertScaleName: 'Dislike Very Much'
        },
        {
          questionLikertScale_ID: null,
          questionLikertScaleName: 'Dislike'
        },
        {
          questionLikertScale_ID: null,
          questionLikertScaleName: 'Moderately Dislike'
        },
        {
          questionLikertScale_ID: null,
          questionLikertScaleName: 'Neutral'
        },
        {
          questionLikertScale_ID: null,
          questionLikertScaleName: 'Moderately Like'
        },
        {
          questionLikertScale_ID: null,
          questionLikertScaleName: 'Like'
        },
        {
          questionLikertScale_ID: null,
          questionLikertScaleName: 'Like Very Much'
        }
      ]
    });
    tempFormTaskSections[sectionIndex].questionName_array = tempQuestionArray;
    setNewQuestionIndex(sectionIndex);
    setEditQuestionId('');
    setEditQuestionIndex(null);
    setDeleteQuestionIndex(null);
    setAddQuestionDisable(true);
    setFormTaskSections(tempFormTaskSections);

    const sectionId = tempFormTaskSections[sectionIndex].questionPaperSection_ID;
    if (sectionId && !openSections.has(sectionId)) {
      toggleOpen(sectionId, true);
    }
  };

  const handleChangeQuestionType = (sectionIndex, questionIndex, questionTypeId, question) => {
    let tempFormTaskSections = formTaskSections.slice();

    question = {
      ...question,
      questionType_ID: questionTypeId,
      oldQuestionType_ID: question.oldQuestionType_ID,
      isEdit: true
    };

    tempFormTaskSections[sectionIndex].questionName_array[questionIndex] = question;

    setFormTaskSections(tempFormTaskSections);
  };

  const handleDuplicateQuestion = (sectionIndex, questionIndex, setCheckBoxRequired) => {
    let tempFormTaskSections = formTaskSections.slice();
    let question = tempFormTaskSections[sectionIndex]?.questionName_array[questionIndex];

    const duplicatedQuestion = {
      ...question,
      isEdit: true,
      question_ID: null,
      PrevQuestion_ID: question.question_ID,

      isLogicApplied: 0,
      isSkipLogicApplied: 0,
      ParentquestionAttached: null,
      selectedQuestion: null,
      selectedQuestionSectionAttached: null,
      selectedChoiceID: null,
      selectedLikerChocieID: null,
      LikertScaleID: null,
      skipSectionAttached: null,
      inputValue: null,
      comparibility_ID: null,
      selectedComparibilityID: null,
      ANDLogic: [],
      ORLogic: []
    };

    tempFormTaskSections[sectionIndex].questionName_array.splice(questionIndex + 1, 0, duplicatedQuestion);
    setFormTaskSections(tempFormTaskSections);
    setIsEditing(true);

    setCheckBoxRequired(duplicatedQuestion.isRequired);
  };

  const handleEditQuestion = (sectionIndex, questionIndex) => {
    let tempFormTaskSections = formTaskSections.slice();

    // Close any currently open question before opening the new one
    tempFormTaskSections = tempFormTaskSections.map((section, sIdx) => ({
      ...section,
      questionName_array: section.questionName_array.map((q, qIdx) => {
        if (q.isEdit && !(sIdx === sectionIndex && qIdx === questionIndex)) {
          return { ...q, isEdit: false };
        }
        return q;
      })
    }));

    let question = tempFormTaskSections[sectionIndex]?.questionName_array[questionIndex];
    question = { ...question, isEdit: true };

    tempFormTaskSections[sectionIndex].questionName_array[questionIndex] = question;
    setFormTaskSections(tempFormTaskSections);
    setIsEditing(true);
  };

  const handleDeleteQuestion = (sectionIndex, questionIndex) => {
    let tempFormTaskSections = formTaskSections.slice();
    let question = tempFormTaskSections[sectionIndex]?.questionName_array[questionIndex];

    // Remove the question from the array
    tempFormTaskSections[sectionIndex].questionName_array.splice(questionIndex, 1);
    setFormTaskSections(tempFormTaskSections);
    setAddQuestionDisable(false);
    // Dispatch only if question_ID is not null
    if (question?.question_ID) {
      setQuestionIndex(questionIndex);
      setDeleteQuestionIndex(sectionIndex);
      setNewQuestionIndex(null);
      let payload = {
        user_ID: loggedUser.userId,
        questions_ID: question.question_ID
      };

      dispatch({
        type: 'REQUEST_DELETE_QUESITON',
        formData: payload
      });
    }
  };

  const handleDeleteSection = ({ sectionId, fallbackIndex }) => {
    const sectionIndex =
      sectionId !== null && sectionId !== undefined
        ? formTaskSections.findIndex(s => s.questionPaperSection_ID === sectionId)
        : fallbackIndex;

    if (sectionIndex < 0 || sectionIndex >= formTaskSections.length) {
      setPopup(false);
      setDeleteTargetSectionKey(null);
      return;
    }

    setDeleteSectionIndex(sectionId);
    let tempFormTaskSections = formTaskSections.slice();
    let section = tempFormTaskSections[sectionIndex];
    let payload = {
      user_ID: loggedUser.userId,
      projectTask_ID: projectTask_ID,
      QuestionPaperSection_ID: section.questionPaperSection_ID
    };

    tempFormTaskSections.splice(sectionIndex, 1);
    setFormTaskSections(tempFormTaskSections);
    if (section?.questionPaperSection_ID) {
      dispatch({
        type: 'REQUEST_DELETE_SECTION',
        formData: payload
      });
    }

    setAddSectionDisbale(false);
    setPopup(false);
    setDeleteTargetSectionKey(null);
  };

  const handleProjectTaskTab = () => {
    handleTaskTab(task.taskType, task.projectTask_ID, task.IsVisited);
  };

  const refreshTask = () => {
    maverickPortalhttp
      .makeRequest(
        `${maverickPortalApiEndPoint.getProjectTask.url}/${loggedUser.userId}/${projectId}/${projectTask_ID}`,
        maverickPortalApiEndPoint.getProjectTask.methodType
      )
      .then(response => {
        const sections = response.data.Response?.GetProjectTaskQuestions?.map(section => ({
          ...section,
          sectionImage: section.presignedUrlSectionImage || '',
          fileType: section.fileType !== undefined && section.fileType !== null ? section.fileType : '',
          sectionName: section.sectionName || '',
          sectionDescription: section.sectionDescription || '',
          timer: section.timer || ''
        })) || [];
        setFormTaskSections(sections);
      });
  };
  const handleProjectTaskNext = () => {
    handleTaskTab(nextTask.taskType, nextTask.projectTask_ID, nextTask.IsVisited);
  };

  const dropAnimation = {
    sideEffects: defaultDropAnimationSideEffects({
      styles: {
        active: {
          opacity: '0.5'
        }
      }
    })
  };
  const animateLayoutChanges = args => defaultAnimateLayoutChanges({ ...args, wasDragging: true });

  const handleTimerChange = (sIndex, value) => {
    // sIndex here is actually the section key (questionPaperSection_ID or tempId)
    setTimerMap(prev => ({ ...prev, [sIndex]: value }));
    // Do NOT touch previewMap, selectedFileMap, or mediaTypeMap here!
    // If there's a valid timer value, set editSectionIndex to show save button
    if (value && Number(value) > 0) {
      // Find the section that has this timer key
      const section = formTaskSections.find((s, idx) => {
        const sectionKey = getSectionKey(s, idx);
        return sectionKey === sIndex;
      });
      if (section && section.questionPaperSection_ID) {
        seteditSectionIndex(section.questionPaperSection_ID);
      }
    }
  };

  const isSectionResponded = section => {
    return Array.isArray(section.questionName_array) && section.questionName_array.some(q => q.isResponded === 1);
  };

  // Helper to get a stable key for a section (ID if exists, else tempId, else sIndex)
  const getSectionKey = (section, sIndex) => section?.questionPaperSection_ID || section?.tempId || sIndex;

  const SortableSection = React.memo(({ section, isOpen, toggleOpen, isDragging, sIndex, crossSectionHoverIndex }) => {
    const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition } = useSortable({
      id: section.questionPaperSection_ID
    });
    const style = {
      transform: CSS.Translate.toString(transform),
      transition,
      border: isDragging ? '4px solid #83A600' : ''
    };

    const [editSaveButton, setEditSaveButton] = useState(false);
    const sectionKey = getSectionKey(section, sIndex);

    return (
      <div ref={setNodeRef} style={style} data-section-id={section.questionPaperSection_ID}>
        {/* Child Questions (Do Not Trigger Toggle) */}
        <React.Fragment key={section.questionPaperSection_ID}>
          <CustomDivider />
          <div className="row">
            <div className="col-md-11">
              {isPopupVisible && deletesectionmessage && sIndex === 0 && (
                <center>
                  <div style={{ color: popupColor, fontWeight: 'bold', textAlign: 'left' }}>
                    <div>{deletesectionmessage}</div>
                  </div>
                </center>
              )}
              <div className="d-flex flex-column align-items-center" style={{ float: 'right' }}>
                <CustomButton
                  variant="outlined"
                  buttonColor="var(--greyBlue)"
                  className=""
                  onClick={e => {
                    setPopup(true);
                    setDeleteTargetSectionKey(getSectionKey(section, sIndex));
                  }}
                  style={{ color: 'var(--greyBlue)', backgroundColor: 'white', fontSize: '15px' }}
                >
                  <Icon className="material-icons-round" style={{ color: 'var(--greyBlue)', marginTop: '1.5px' }}>
                    delete
                  </Icon>
                  Delete Section
                </CustomButton>
                <CustomPopup
                  onClose={closeHandler}
                  showed={popup && deleteTargetSectionKey === getSectionKey(section, sIndex)}
                  heading="Confirm delete"
                  secondheading="Are you sure you want to delete this Section?"
                  secondheading2="Once deleted, it cant be retrieved."
                >
                  <CustomButtont
                    fontSize="14px"
                    fontWeight="300"
                    color="black"
                    size="medium"
                    style={{ justifyContent: 'space-between', marginRight: '20px' }}
                    onClick={e => {
                      e.stopPropagation();
                      handleDeleteSection({
                        sectionId: section.questionPaperSection_ID,
                        fallbackIndex: sIndex
                      });
                    }}
                  >
                    Delete
                  </CustomButtont>
                </CustomPopup>
                {!section.isNew &&
                  section.questionPaperSection_ID &&
                  (section.questionName_array || []).length >= 2 && (
                    <div
                      className="d-flex align-items-end justify-content-center"
                      title={
                        isSectionShuffleDisabled(section)
                          ? 'Cannot randomize when conditions are applied on questions'
                          : undefined
                      }
                      style={{
                        marginTop: '8px',
                        width: '100%',
                        cursor: isSectionShuffleDisabled(section) ? 'not-allowed' : 'pointer'
                      }}
                      onClick={e => e.stopPropagation()}
                      onPointerDown={e => e.stopPropagation()}
                    >
                      <label
                        className="switch"
                        style={{
                          marginLeft: 0,
                          marginRight: 0,
                          marginTop: 0,
                          marginBottom: 0,
                          cursor: isSectionShuffleDisabled(section) ? 'not-allowed' : 'pointer'
                        }}
                      >
                        <input
                          type="checkbox"
                          name={`shuffleQuestion-${section.questionPaperSection_ID || sIndex}`}
                          checked={getSectionShuffleValue(section) === 1}
                          onChange={e => handleShuffleSectionToggle(sIndex, e.target.checked)}
                          disabled={isSectionShuffleDisabled(section)}
                        />
                        <span
                          className="slider round"
                          style={{
                            cursor: isSectionShuffleDisabled(section) ? 'not-allowed' : 'pointer'
                          }}
                        ></span>
                      </label>
                      <span
                        style={{
                          color: isSectionShuffleDisabled(section) ? 'var(--lightestGrey)' : 'var(--greyBlue)',
                          fontSize: '0.8125rem',
                          fontWeight: 500,
                          marginLeft: '8px',
                          lineHeight: 1,
                          whiteSpace: 'nowrap',
                          paddingBottom: '1px',
                          cursor: isSectionShuffleDisabled(section) ? 'not-allowed' : 'pointer'
                        }}
                      >
                        Shuffle Questions
                      </span>
                    </div>
                  )}
              </div>

              <div className="section-wrapper ">
                <div className="row g-3 align-items-start" style={{ cursor: 'pointer' }}>
                  <div className="col-auto flex-shrink-0">
                    {/* Drag Handle (Only This is Draggable) */}
                    <div className="d-flex align-items-start">
                      <button
                        ref={setActivatorNodeRef}
                        {...listeners}
                        {...attributes}
                        style={{
                          cursor: 'grab',
                          padding: 0,
                          margin: '0.5rem 0 0 0',
                          border: 0,
                          background: 'transparent',
                          lineHeight: 1,
                          height: '1.125rem',
                          width: '1.125rem',
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0
                        }}
                      >
                        <PiDotsSixVerticalBold size={18} />
                      </button>
                      <Heading
                        type="5"
                        className="mt-2 mb-0"
                        color="var(--seaBlue)"
                        onClick={() => toggleOpen(section.questionPaperSection_ID)}
                      >
                        Section {+sIndex + +1} {isOpen ? '▼' : '▶'}
                      </Heading>
                    </div>
                  </div>
                  {formTaskSections[sIndex].isNew ? (
                    <div className="col" style={{ minWidth: 0 }}>
                      <SectionDetailsEditor
                        sectionKey={sectionKey}
                        sectionName={section.sectionName}
                        sectionDescription={section.sectionDescription}
                        isVirtualKeyboardEnabled={isVirtualKeyboardEnabled}
                        onSectionFieldChange={(field, value) => {
                          const tempFormTaskSections = formTaskSections;
                          if (tempFormTaskSections[sIndex]) {
                            tempFormTaskSections[sIndex][field] = value;
                          }
                        }}
                        onSectionEdit={() => {}}
                      />
                      {sectionNameError && (
                        <p style={{ color: 'red', fontSize: '11px' }}>Please fill in the section name.</p>
                      )}
                      <MediaUploadPreview
                        onSelectFile={onSelectFile}
                        handleRemoveFile={handleRemoveFile}
                        fileError={fileError}
                        preview={previewMap[getSectionKey(section, sIndex)]}
                        mediaType={mediaTypeMap[getSectionKey(section, sIndex)]}
                        presignedUrlSectionImage={section.presignedUrlSectionImage}
                        sectionFileType={section.fileType}
                        editSection={section.questionPaperSection_ID}
                        setSelectedFile={selectedFile =>
                          setSelectedFileMap(prev => ({ ...prev, [getSectionKey(section, sIndex)]: selectedFile }))
                        }
                        setMediaType={mediaType =>
                          setMediaTypeMap(prev => ({ ...prev, [getSectionKey(section, sIndex)]: mediaType }))
                        }
                        clearSectionMedia={clearSectionMedia}
                        sIndex={sIndex}
                        disabled={isSectionResponded(section)}
                        fileName={fileNameMap[getSectionKey(section, sIndex)]}
                      />
                      {(previewMap[getSectionKey(section, sIndex)] || section.presignedUrlSectionImage) && (
                        <TimerInput
                          timer={timerMap[getSectionKey(section, sIndex)]}
                          onTimerChange={handleTimerChange}
                          sIndex={getSectionKey(section, sIndex)}
                          editSection={section.questionPaperSection_ID}
                          seteditSectionIndex={seteditSectionIndex}
                          disabled={isSectionResponded(section)}
                          timerMap={timerMap[getSectionKey(section, sIndex)]}
                          timerMapButton={timerMapButton}
                          setTimerMapButton={setTimerMapButton}
                        />
                      )}
                      {sectionTimeErrorMap[getSectionKey(section, sIndex)] &&
                        (!timerMap[getSectionKey(section, sIndex)] ||
                          Number(timerMap[getSectionKey(section, sIndex)]) <= 0) && (
                          <p style={{ color: 'red', fontSize: '11px' }}>Please set the Time.</p>
                        )}
                      {showSaveButton && (
                        <>
                          <CustomButton
                            variant="outlined"
                            buttonColor="var(--sapGreen)"
                            className="mt-3"
                            data-virtualkeyboard-ignore="true"
                            onClick={e => {
                              e.stopPropagation();
                              if (!section.sectionName.trim()) {
                                // alert('Please fill in the section name.')
                                setSectionNameError(true);
                              } else if (
                                (previewMap[getSectionKey(section, sIndex)] || section.presignedUrlSectionImage) &&
                                (!timerMap[getSectionKey(section, sIndex)] ||
                                  Number(timerMap[getSectionKey(section, sIndex)]) <= 0)
                              ) {
                                setSectionNameError(false);
                                setSectionTimeErrorMap(prev => ({ ...prev, [getSectionKey(section, sIndex)]: true }));
                                return;
                              } else {
                                setSectionNameError(false);
                                // Update section in formTaskSections and set isNew to false
                                const updatedSections = [...formTaskSections];
                                updatedSections[sIndex] = {
                                  ...updatedSections[sIndex],
                                  isNew: false // Mark section as no longer new
                                };
                                setFormTaskSections(updatedSections);
                                storeSection(sIndex, 'sectionDescription', e.target.value);
                                // storeSection(index, 'sectionName', e.target.value)
                                setSectionTimeErrorMap(prev => ({ ...prev, [getSectionKey(section, sIndex)]: false }));
                                setisNewSectionForm(true);
                                setAddSectionDisbale(false);
                                setShowSaveButton(false);
                              }
                            }}
                          >
                            <Icon>save</Icon>
                            Save Section
                          </CustomButton>
                        </>
                      )}
                    </div>
                  ) : (
                    <div className="col" style={{ minWidth: 0 }}>
                      <SectionDetailsEditor
                        sectionKey={sectionKey}
                        sectionName={section.sectionName}
                        sectionDescription={section.sectionDescription}
                        isVirtualKeyboardEnabled={isVirtualKeyboardEnabled}
                        onSectionFieldChange={(field, value) => {
                          const tempFormTaskSections = formTaskSections;
                          if (tempFormTaskSections[sIndex]) {
                            tempFormTaskSections[sIndex][field] = value;
                          }
                        }}
                        onSectionEdit={() => {
                          setEditSaveButton(true);
                        }}
                      />
                      {editSectionIndex === section.questionPaperSection_ID && sectionNameError && (
                        <p style={{ color: 'red', fontSize: '11px' }}>Please fill in the section name.</p>
                      )}
                      <MediaUploadPreview
                        onSelectFile={onSelectFile}
                        handleRemoveFile={handleRemoveFile}
                        fileError={fileError}
                        preview={previewMap[getSectionKey(section, sIndex)]}
                        mediaType={mediaTypeMap[getSectionKey(section, sIndex)]}
                        presignedUrlSectionImage={section.presignedUrlSectionImage}
                        sectionFileType={section.fileType}
                        editSection={section.questionPaperSection_ID}
                        setSelectedFile={selectedFile =>
                          setSelectedFileMap(prev => ({ ...prev, [getSectionKey(section, sIndex)]: selectedFile }))
                        }
                        setMediaType={mediaType =>
                          setMediaTypeMap(prev => ({ ...prev, [getSectionKey(section, sIndex)]: mediaType }))
                        }
                        clearSectionMedia={clearSectionMedia}
                        sIndex={sIndex}
                        disabled={isSectionResponded(section)}
                        fileName={fileNameMap[getSectionKey(section, sIndex)]}
                      />
                      {(previewMap[getSectionKey(section, sIndex)] || section.presignedUrlSectionImage) && (
                        <TimerInput
                          timer={timerMap[getSectionKey(section, sIndex)]}
                          onTimerChange={handleTimerChange}
                          sIndex={getSectionKey(section, sIndex)}
                          editSection={section.questionPaperSection_ID}
                          seteditSectionIndex={seteditSectionIndex}
                          disabled={isSectionResponded(section)}
                          timerMap={timerMap[getSectionKey(section, sIndex)]}
                          timerMapButton={timerMapButton}
                          setTimerMapButton={setTimerMapButton}
                        />
                      )}

                      {sectionTimeErrorMap[getSectionKey(section, sIndex)] &&
                        (!timerMap[getSectionKey(section, sIndex)] ||
                          Number(timerMap[getSectionKey(section, sIndex)]) <= 0) && (
                          <p style={{ color: 'red', fontSize: '11px' }}>Please set the Time.</p>
                        )}
                      {(editSectionIndex === section.questionPaperSection_ID || editSaveButton) && (
                        <>
                          <CustomButton
                            variant="outlined"
                            buttonColor="var(--sapGreen)"
                            className="mt-3"
                            data-virtualkeyboard-ignore="true"
                            onClick={e => {
                              e.stopPropagation();
                              // storeSection(index, 'sectionDescription', e.target.value)
                              if (!section.sectionName.trim()) {
                                setSectionNameError(true);
                              } else if (
                                (previewMap[getSectionKey(section, sIndex)] || section.presignedUrlSectionImage) &&
                                (!timerMap[getSectionKey(section, sIndex)] ||
                                  Number(timerMap[getSectionKey(section, sIndex)]) <= 0)
                              ) {
                                setSectionNameError(false);
                                setSectionTimeErrorMap(prev => ({ ...prev, [getSectionKey(section, sIndex)]: true }));
                                return;
                              } else {
                                setSectionNameError(false);
                                updateSection(sIndex, 'sectionName', section.sectionName);
                                // updateSection(sIndex, 'sectionDescription', section.sectionDescription);
                                setSectionTimeErrorMap(prev => ({ ...prev, [getSectionKey(section, sIndex)]: false }));
                                setShowSaveButton(false);
                                setEditSaveButton(false);
                                // Don't reset editSectionIndex immediately - let the useEffect handle it after API response
                              }
                            }}
                          >
                            <Icon>save</Icon>
                            Save Section
                          </CustomButton>
                        </>
                      )}
                      {editSectionIndex === section.questionPaperSection_ID && sectionmessage && (
                        <center>
                          <div style={{ color: popupColor, fontWeight: 'bold', textAlign: 'left' }}>
                            <div>{sectionmessage}</div>
                          </div>
                        </center>
                      )}
                    </div>
                  )}
                </div>
                {isOpen && !isDragging && (
                  <>
                    <div className="questions-list mt-3">
                      <SortableContext
                        items={section.questionName_array.map(q => q.question_ID).filter(id => id !== null)}
                      >
                        <React.Fragment key={section.questionPaperSection_ID}>
                          <div className="questions-list mt-3">
                            {section.questionName_array.map((question, qIndex) => (
                              <React.Fragment key={question.question_ID}>
                                <div
                                  style={{
                                    height: crossSectionHoverIndex === qIndex ? `${QUESTION_DROP_PLACEHOLDER_HEIGHT}px` : '0px',
                                    opacity: crossSectionHoverIndex === qIndex ? 1 : 0,
                                    overflow: 'hidden',
                                    margin: crossSectionHoverIndex === qIndex ? '8px 0' : '0px',
                                    border: 'none',
                                    borderRadius: '8px',
                                    background: 'var(--white)',
                                    flexShrink: 0,
                                    transition: 'height 0.12s ease, opacity 0.12s ease, margin 0.12s ease'
                                  }}
                                />
                                <SortableQuestion
                                  key={question.question_ID}
                                  question={question}
                                  qIndex={qIndex}
                                  section={section}
                                  sIndex={sIndex}
                                  QuestionIndex={QuestionIndex}
                                />
                              </React.Fragment>
                            ))}
                            <div
                              style={{
                                height: crossSectionHoverIndex === section.questionName_array.length ? `${QUESTION_DROP_PLACEHOLDER_HEIGHT}px` : '0px',
                                opacity: crossSectionHoverIndex === section.questionName_array.length ? 1 : 0,
                                overflow: 'hidden',
                                margin: crossSectionHoverIndex === section.questionName_array.length ? '8px 0' : '0px',
                                border: 'none',
                                borderRadius: '8px',
                                background: 'var(--white)',
                                flexShrink: 0,
                                transition: 'height 0.12s ease, opacity 0.12s ease, margin 0.12s ease'
                              }}
                            />
                          </div>
                        </React.Fragment>
                      </SortableContext>
                    </div>

                    {newQuestionIndex === sIndex && questionmessage && (
                      <center>
                        <div style={{ color: popupColor, fontWeight: 'bold', textAlign: 'left' }}>
                          <div>{questionmessage}</div>
                        </div>
                      </center>
                    )}

                    {isPopupVisible && deletesectionmessage && deleteSectionIndex === section.questionPaperSection_ID && (
                      <center>
                        <div style={{ color: popupColor, fontWeight: 'bold', textAlign: 'left' }}>
                          <div>{deletesectionmessage}</div>
                        </div>
                      </center>
                    )}
                  </>
                )}

                <CustomButton
                  variant="outlined"
                  buttonColor="var(--sapGreen)"
                  color="var(--sapGreen)"
                  className="mt-5"
                  onClick={() => createQuestion(sIndex)}
                  disabled={section.isNew || addQuestionDisable || isSectionSaveLoading}
                >
                  <Icon>add</Icon>
                  Add Question
                </CustomButton>
              </div>
            </div>
          </div>
        </React.Fragment>
      </div>
    );
  });

  const SortableQuestion = React.memo(({ question, qIndex, section, sIndex, QuestionIndex }) => {
    const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({
      id: question.question_ID,
      animateLayoutChanges,
      transition: {
        duration: 200,
        easing: 'cubic-bezier(0.25, 1, 0.5, 1)'
      }
    });

    const questioncount = section?.questionName_array?.length ?? 1;
    const stackz = 10 + Math.max(0, questioncount - 1 - qIndex);

    const style = {
      transform: CSS.Translate.toString(transform),
      transition: isDragging ? 'transform 0.2s ease' : transition,
      background: 'white',
      position: 'relative',
      zIndex: isDragging ? 1000 : stackz,
      opacity: isDragging ? 0 : 1,
      // boxShadow: isDragging ? '0 4px 8px rgba(0,0,0,0.1)' : 'none',
      // borderRadius: isDragging ? '4px' : '0',
      cursor: isDragging ? 'grabbing' : 'default',
      transformOrigin: 'center center',
      willChange: 'transform, opacity'
    };

    return (
      <div ref={setNodeRef} style={style}>
        <div
          key={question.question_ID}
          index={qIndex}
          onPointerDown={e => {
            // While editing, don't interfere with pointer events so inputs/buttons work normally
            if (isEditing) return;
            e.stopPropagation();
            e.preventDefault();
          }}
          style={{
            position: 'relative',
            transform: isDragging ? 'scale(1.02)' : 'scale(1)',
            transition: 'transform 0.2s ease'
          }}
        >
          <button
            ref={setActivatorNodeRef}
            {...(!isEditing ? { ...listeners, ...attributes } : {})}
            style={{
              cursor: isEditing ? 'default' : isDragging ? 'grabbing' : 'grab',
              padding: 0,
              margin: 0,
              border: 0,
              position: 'absolute',
              zIndex: 100,
              top: '50%',
              transform: 'translateY(-50%)',
              opacity: isEditing ? 0 : isDragging ? 0 : 0.7,
              transition: 'opacity 0.2s ease',
              backgroundColor: 'transparent',
              pointerEvents: isEditing ? 'none' : 'auto'
            }}
          >
            <PiDotsSixVerticalBold />
          </button>
          <DesignQuestion
            key={qIndex}
            sectionIndex={sIndex}
            setSwitchValue={setSwitchValue}
            switchvalue={switchvalue}
            questionIndex={qIndex}
            sectionId={section.questionPaperSection_ID}
            questionArrayLength={section?.questionName_array?.length}
            question={question}
            questionTypes={questionTypes}
            ValueTypeChoices={ValueTypeChoices}
            handleChangeQuestionType={handleChangeQuestionType}
            handleDuplicateQuestion={handleDuplicateQuestion}
            handleEditQuestion={handleEditQuestion}
            handleSetEditing={handleSetEditing}
            handleDeleteQuestion={handleDeleteQuestion}
            handleProjectTaskTab={handleProjectTaskTab}
            refreshTask={refreshTask}
            isInterview={task.taskType !== 'Form'}
            setEditQuestionIndex={setEditQuestionIndex}
            setNewQuestionIndex={setNewQuestionIndex}
            setDeleteQuestionIndex={setDeleteQuestionIndex}
            enableEdit={editQuestionId == question?.question_ID}
            setEditQuestionId={setEditQuestionId}
            setAddQuestionDisable={setAddQuestionDisable}
            setQuestionIndex={setQuestionIndex}
            questionmessage={questionmessage}
            popupColor={popupColor}
            showCheckbox={true}
            setFormTaskSections={setFormTaskSections}
            formTaskSections={formTaskSections}
            task={task}
            choiceNameParentQuestions={choiceNameParentQuestions}
            isVirtualKeyboardEnabled={isVirtualKeyboardEnabled}
            sectionShuffleEnabled={getSectionShuffleValue(section)}
          />
        </div>
      </div>
    );
  });

  const renderSortableItemDragOverlay = id => {
    // Find the question and its section
    let foundSection = null;
    let foundQuestion = null;
    let foundSectionIndex = -1;
    let foundQuestionIndex = -1;

    formTaskSections.forEach((section, sIndex) => {
      const qIndex = section.questionName_array.findIndex(q => q.question_ID === id);
      if (qIndex !== -1) {
        foundSection = section;
        foundQuestion = section.questionName_array[qIndex];
        foundSectionIndex = sIndex;
        foundQuestionIndex = qIndex;
      }
    });

    if (!foundQuestion) return null;

    return (
      <div
        style={{
          background: 'white',
          transform: 'scale(1.02)',
          opacity: 1,
          transition: 'transform 0.2s ease',
          width: '100%',
          borderLeft: '3px solid #9EBE21',
          borderRadius: '10px'
        }}
      >
        <DesignQuestion
          key={foundQuestion.question_ID}
          sectionIndex={foundSectionIndex}
          setSwitchValue={setSwitchValue}
          switchvalue={switchvalue}
          questionIndex={foundQuestionIndex}
          sectionId={foundSection.questionPaperSection_ID}
          questionArrayLength={foundSection?.questionName_array?.length}
          question={foundQuestion}
          questionTypes={questionTypes}
          ValueTypeChoices={ValueTypeChoices}
          handleChangeQuestionType={handleChangeQuestionType}
          handleDuplicateQuestion={handleDuplicateQuestion}
          handleEditQuestion={handleEditQuestion}
          handleSetEditing={handleSetEditing}
          handleDeleteQuestion={handleDeleteQuestion}
          handleProjectTaskTab={handleProjectTaskTab}
          isInterview={task.taskType !== 'Form'}
          setEditQuestionIndex={setEditQuestionIndex}
          setNewQuestionIndex={setNewQuestionIndex}
          setDeleteQuestionIndex={setDeleteQuestionIndex}
          enableEdit={editQuestionId == foundQuestion?.question_ID}
          setEditQuestionId={setEditQuestionId}
          setAddQuestionDisable={setAddQuestionDisable}
          setQuestionIndex={setQuestionIndex}
          questionmessage={questionmessage}
          popupColor={popupColor}
          showCheckbox={true}
          setFormTaskSections={setFormTaskSections}
          formTaskSections={formTaskSections}
          task={task}
          choiceNameParentQuestions={choiceNameParentQuestions}
          isVirtualKeyboardEnabled={isVirtualKeyboardEnabled}
          sectionShuffleEnabled={getSectionShuffleValue(foundSection)}
        />
      </div>
    );
  };

  const clearSectionMedia = (sectionId, sIndex) => {
    // Clear all state maps completely like in CriteriaBudgetScreen
    setFormTaskSections(prevSections =>
      prevSections.map(section =>
        section.questionPaperSection_ID === sectionId
          ? { ...section, presignedUrlSectionImage: '', fileType: '', timer: '' }
          : section
      )
    );
    setPreviewMap({});
    setMediaTypeMap({});
    setSelectedFileMap({});
    setFileNameMap({});
    // Clear only the timer for the specific section, not all timers
    setTimerMap(prev => ({ ...prev, [sectionId]: '' }));
  };

  if (shouldShowInitialLoader) {
    return (
      <div className="d-flex justify-content-center py-5">
        <PageLoader size="100px" />
      </div>
    );
  }

  return (
    <>
      <section className="form-notification-section ">
        <div className="row py-2">
          <div className="col-md-12">
            <Heading color={'var(--deepBlue)'} fontSize="1.125rem" fontWeight="800">
              {formTask.taskName}
            </Heading>

            <div className="d-flex align-items-start gap-2 mt-1">
              <TextArea
                showCharacters={false}
                fontSize="0.75rem"
                fontWeight="500"
                placeholder="Add task description"
                value={formTask.taskDescription}
                onChange={e => {
                  const updatedTask = { ...formTask, taskDescription: e.target.value };
                  setFormTask(updatedTask);
                  onTaskDescriptionEdit(updatedTask, e.target.value);
                }}
                style={{ flex: 1 }}
              />
              {isTaskDescriptionEditing && (
                <CustomButton
                  variant="outlined"
                  buttonColor="var(--sapGreen)"
                  onClick={onSaveTaskDescription}
                  style={{ minWidth: 'auto', height: 'fit-content' }}
                >
                  <Icon>save</Icon>
                  Save
                </CustomButton>
              )}
            </div>
            <div className="d-flex justify-content-end mt-2">
              <div>
                <label className="switch ml-3 permission-toggle">
                  <input
                    type="checkbox"
                    name="virtualKeyboard"
                    checked={isVirtualKeyboardEnabled}
                    onChange={e => setIsVirtualKeyboardEnabled(e.target.checked)}
                  />
                  <span className="slider round"></span>
                </label>
                <span
                  className="font-weight-bold ml-2 mb-1"
                  style={{ color: 'var(--greyBlue)', position: 'relative', top: '-5px' }}
                >
                  Use Virtual Keyboard
                </span>
              </div>
            </div>
          </div>
        </div>

        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          measuring={{
            droppable: {
              strategy: MeasuringStrategy.Always
            }
          }}
          autoScroll={true}
          onDragStart={handleDragStart}
          onDragOver={handleDragOver}
          onDragEnd={handleDragEnd}
        >
          <SortableContext items={formTaskSections?.map((s, i) => getSectionKey(s, i))} strategy={verticalListSortingStrategy}>
            {formTaskSections.map((section, sIndex) => {
              const sectionKey = getSectionKey(section, sIndex);
              return (
                <SortableSection
                  key={sectionKey}
                  section={section}
                  isOpen={openSections.has(sectionKey)}
                  toggleOpen={toggleOpen}
                  isDragging={draggingItem === sectionKey}
                  sIndex={sIndex}
                  crossSectionHoverIndex={
                    crossSectionHover && crossSectionHover.sectionId === section.questionPaperSection_ID
                      ? crossSectionHover.index
                      : null
                  }
                />
              );
            })}
          </SortableContext>
          <DragOverlay dropAnimation={dropAnimation}>
            {draggingItem ? renderSortableItemDragOverlay(draggingItem) : null}
          </DragOverlay>

          <CustomDivider />

          <CustomButton
            variant="outlined"
            buttonColor="var(--sapGreen)"
            className="mt-3"
            onClick={e => createSection()}
            disabled={addSectionDisable}
          >
            <Icon>add</Icon>
            Add New Section
          </CustomButton>
          {isNewSectionForm && sectionmessage && (
            <center>
              <div style={{ color: popupColor, fontWeight: 'bold', textAlign: 'left' }}>
                <div>{sectionmessage}</div>
              </div>
            </center>
          )}
        </DndContext>

        <div className="d-flex flex-column align-items-center justify-content-center mt-5">
          <Heading type="4">Well done!</Heading>
          <Heading type="5">Your progress so far has been saved</Heading>
          <div className="d-flex ">
            <Link to={`/project/${projectId}/invite-recruit`}>
              <Button size="mid" className="mt-4 mr-2" buttonColor="var(--sapGreen)">
                Recruit Respondents
              </Button>
            </Link>

            <Button size="mid" className="mt-4 ml-2" buttonColor="var(--sapGreen)" onClick={handleProjectTaskNext}>
              Design {nextTask.taskType != 'Video' ? nextTask.taskType : 'Interview'}
            </Button>
            <Link to={{ pathname: '/project/' + projectId + '/field' }} style={{ marginLeft: '15px' }}>
              <Button size="mid" className="mt-4 mr-2" buttonColor="var(--sapGreen)">
                Go To Field
              </Button>
            </Link>
          </div>
        </div>
        <ToastContainer hideProgressBar={true} />
      </section>
    </>
  );
}
